import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Clock, CheckCircle2, DollarSign, Plus, X, ChevronDown, Trash2, Edit, Check, XCircle } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';

const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

function AddEmployeeModal({ onClose, onSave }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [role, setRole] = useState('employee');
  const [spendingLimit, setSpendingLimit] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSaving(true);
    try {
      await onSave({ name, email, department, role, spending_limit: parseFloat(spendingLimit) || 0 });
      onClose();
    } catch (e) { console.error('Save employee error:', e); }
    setSaving(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="add-employee-modal">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden shadow-xl">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">Add Employee</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500" placeholder="John Smith" required data-testid="employee-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500" placeholder="john@company.com" required data-testid="employee-email-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
            <input value={department} onChange={e => setDepartment(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500" placeholder="e.g. Marketing" data-testid="employee-department-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
            <div className="relative">
              <select value={role} onChange={e => setRole(e.target.value)} className="w-full appearance-none px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 cursor-pointer" data-testid="employee-role-select">
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="approver">Approver</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Spending Limit ($)</label>
            <input type="number" step="0.01" value={spendingLimit} onChange={e => setSpendingLimit(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500" placeholder="500.00" data-testid="employee-limit-input" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50" data-testid="employee-save-button">{saving ? 'Adding...' : 'Add Employee'}</button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default function Employees() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: stats = {} } = useQuery({
    queryKey: ['employee-stats'],
    queryFn: async () => { const { data } = await api.get('/api/employees/stats'); return data; }
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: async () => { const { data } = await api.get('/api/employees'); return data; }
  });

  const { data: approvals = [] } = useQuery({
    queryKey: ['expense-approvals'],
    queryFn: async () => { const { data } = await api.get('/api/expense-approvals'); return data; }
  });

  const addEmployee = useMutation({
    mutationFn: (data) => api.post('/api/employees', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employee-stats'] });
      toast.success('Employee added');
    },
    onError: () => toast.error('Failed to add employee'),
  });

  const deleteEmployee = useMutation({
    mutationFn: (id) => api.delete(`/api/employees/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employee-stats'] });
      toast.success('Employee removed');
    },
  });

  const updateApproval = useMutation({
    mutationFn: ({ id, status }) => api.put(`/api/expense-approvals/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['employee-stats'] });
      toast.success('Approval updated');
    },
  });

  const statCards = [
    { label: 'Total Employees', value: stats.total_employees || 0, icon: Users, bgColor: 'bg-emerald-100', iconColor: 'text-emerald-600', format: false },
    { label: 'Pending Approvals', value: stats.pending_approvals || 0, icon: Clock, bgColor: 'bg-amber-100', iconColor: 'text-amber-500', format: false },
    { label: 'Approved This Month', value: stats.approved_this_month || 0, icon: CheckCircle2, bgColor: 'bg-blue-100', iconColor: 'text-blue-500', format: false },
    { label: 'Total Approved', value: stats.total_approved || 0, icon: DollarSign, bgColor: 'bg-purple-100', iconColor: 'text-purple-500', format: true },
  ];

  const pendingApprovals = approvals.filter(a => a.status === 'pending');
  const approvalHistory = approvals.filter(a => a.status !== 'pending');

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-8" data-testid="employees-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="employees-title">Employees</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage team members and approve expense submissions</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
          data-testid="add-employee-button"
        >
          <Plus className="w-4 h-4" />
          Add Employee
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm"
            data-testid={`employee-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`w-11 h-11 rounded-xl ${stat.bgColor} flex items-center justify-center mb-3`}>
              <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
            </div>
            <p className="text-3xl font-bold text-slate-900">{stat.format ? fmt(stat.value) : stat.value}</p>
            <p className="text-sm text-slate-500 mt-0.5">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="approvals">
        <TabsList className="bg-white border border-slate-200 mb-6 h-11">
          <TabsTrigger value="approvals" className="text-slate-500 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="employees-tab-approvals">Expense Approvals</TabsTrigger>
          <TabsTrigger value="team" className="text-slate-500 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="employees-tab-team">Team Members</TabsTrigger>
          <TabsTrigger value="history" className="text-slate-500 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="employees-tab-history">History</TabsTrigger>
        </TabsList>

        {/* Expense Approvals Tab */}
        <TabsContent value="approvals" data-testid="employees-approvals-tab">
          {pendingApprovals.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 flex flex-col items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mb-3" />
              <p className="text-slate-500 text-sm">No pending expense approvals</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-100">
                    <th className="px-5 py-3">Employee</th>
                    <th className="px-5 py-3">Description</th>
                    <th className="px-5 py-3">Amount</th>
                    <th className="px-5 py-3">Category</th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pendingApprovals.map(a => (
                    <tr key={a.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5 text-sm font-medium text-slate-900">{a.employee_id}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{a.description}</td>
                      <td className="px-5 py-3.5 text-sm font-medium text-slate-900">{fmt(a.amount)}</td>
                      <td className="px-5 py-3.5"><span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">{a.category}</span></td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{a.created_at ? format(new Date(a.created_at), 'dd MMM yyyy') : '-'}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => updateApproval.mutate({ id: a.id, status: 'approved' })} className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50 transition-colors" title="Approve"><Check className="w-4 h-4" /></button>
                          <button onClick={() => updateApproval.mutate({ id: a.id, status: 'rejected' })} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors" title="Reject"><XCircle className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Team Members Tab */}
        <TabsContent value="team" data-testid="employees-team-tab">
          {employees.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 flex flex-col items-center justify-center">
              <Users className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No team members yet</p>
              <button onClick={() => setShowAddModal(true)} className="mt-3 text-emerald-600 hover:text-emerald-700 text-sm font-medium">+ Add your first employee</button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-100">
                    <th className="px-5 py-3">Employee</th>
                    <th className="px-5 py-3">Department</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3">Spending Limit</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {employees.map(emp => (
                    <tr key={emp.id} className="hover:bg-slate-50 transition-colors" data-testid={`employee-row-${emp.id}`}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold text-sm">{emp.name?.[0]?.toUpperCase() || 'E'}</div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{emp.name}</p>
                            <p className="text-xs text-slate-400">{emp.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{emp.department || '-'}</td>
                      <td className="px-5 py-3.5"><span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 capitalize">{emp.role}</span></td>
                      <td className="px-5 py-3.5 text-sm text-slate-900">{emp.spending_limit ? fmt(emp.spending_limit) : '-'}</td>
                      <td className="px-5 py-3.5"><span className={`inline-flex items-center gap-1.5 text-xs font-medium ${emp.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`}><span className={`w-2 h-2 rounded-full ${emp.status === 'active' ? 'bg-emerald-400' : 'bg-slate-300'}`} />{emp.status === 'active' ? 'Active' : 'Inactive'}</span></td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => { if (window.confirm(`Remove ${emp.name}?`)) deleteEmployee.mutate(emp.id); }} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Remove"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" data-testid="employees-history-tab">
          {approvalHistory.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 flex flex-col items-center justify-center">
              <Clock className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No approval history yet</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-100">
                    <th className="px-5 py-3">Description</th>
                    <th className="px-5 py-3">Amount</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {approvalHistory.map(a => (
                    <tr key={a.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5 text-sm text-slate-900">{a.description}</td>
                      <td className="px-5 py-3.5 text-sm font-medium text-slate-900">{fmt(a.amount)}</td>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${a.status === 'approved' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>{a.status === 'approved' ? 'Approved' : 'Rejected'}</span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{a.approved_at ? format(new Date(a.approved_at), 'dd MMM yyyy') : a.created_at ? format(new Date(a.created_at), 'dd MMM yyyy') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Employee Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddEmployeeModal
            onClose={() => setShowAddModal(false)}
            onSave={(data) => addEmployee.mutateAsync(data)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
